import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { ReferenceField } from '@1po/1po-bff-fe-spec/generated/estimate/request/UpdateReference';
import { LinkedReference, Reference } from '@1po/1po-bff-fe-spec/generated/estimate/response/GetEstimate';
import { useTheme } from 'styled-components';
import { DotCircleIcon } from 'assets/icons';
import { removeReference, updateReference } from 'domains/estimate/Estimate.store';
import { ClientView, GarageView } from 'domains/user';
import { EstimateGarageMargin } from 'pages/EstimatePage/TableSection/EstimateGarageMargin';
import { EstimateGaragePrice } from 'pages/EstimatePage/TableSection/EstimateGaragePrice';
import { EstimateMarginAlert } from 'pages/EstimatePage/TableSection/EstimateMarginAlert';
import { IncompatibleReferenceIndicator } from 'pages/EstimatePage/TableSection/IncompatibleReferenceIndicator';
import {
  DiscountInput,
  EstimatePriceInput,
  GetTitle,
  QuantityInput,
  TextInput,
  VatInput,
} from 'pages/EstimatePage/TableSection/tableComponents';
import {
  ExpandedReferenceDotCircleWrapper,
  ExpandedReferenceWrapper,
  LinkedReferencesBackgroundWrapper,
  STable,
} from 'pages/EstimatePage/TableSection/TableSection.styled';
import { Flex, Icon, MarginBox, Pipeline, Text } from 'UI';
import { getCondArrayItem, textFormatter } from 'utils';

export function LinkedReferenceTable(
  estimateId: string,
  parentReference: Reference,
  currency: string,
  sparePartsView?: string,
) {
  const theme = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isClientView = sparePartsView === ClientView;

  function removeLinkedRow(itemId: string) {
    dispatch(removeReference({ itemId, parentItemId: parentReference.itemId }));
  }

  const handleUpdate = (field: ReferenceField, itemId: string) => (newValue: string) => {
    dispatch(updateReference({ itemId, parentItemId: parentReference.itemId, field, newValue }));
  };

  if (!parentReference.linkedReferences || parentReference.linkedReferences.length === 0) {
    return null;
  }

  const tableColumns = [
    {
      title: GetTitle(t('catalog.reference', 'Reference')),
      dataIndex: 'referenceNumber',
      width: '8vw',
      render: function rowSelector(referenceNumber: string, row: LinkedReference) {
        const isMarginNegativeOrZero = Number(row.margin) <= 0;
        return (
          <Flex justify={'center'} align={'center'}>
            <TextInput
              value={referenceNumber}
              saveFunction={() => undefined}
              removeRow={() => removeLinkedRow(row.itemId)}
              readOnly={!row.isCustom}
              minWidth={140}
              isInvalid={isClientView && isMarginNegativeOrZero}
            />
            <IncompatibleReferenceIndicator isCustom={row.isCustom} isCompatible={row.isCompatible} />
          </Flex>
        );
      },
    },
    {
      title: GetTitle(t('common.designation', 'Designation')),
      dataIndex: 'designation',
      render: function rowSelector(designation: string, row: LinkedReference) {
        const isMarginNegativeOrZero = Number(row.margin) <= 0;
        return (
          <TextInput
            value={designation}
            saveFunction={() => undefined}
            readOnly={!row.isCustom}
            isInvalid={isClientView && isMarginNegativeOrZero}
          />
        );
      },
    },
    {
      title: GetTitle(t('common.price.unit_price', 'Unit Price')),
      dataIndex: 'unitPrice',
      width: '10vw',
      render: function rowSelector(unitPrice: string, row: LinkedReference) {
        const isMarginNegativeOrZero = Number(row.margin) <= 0;
        return (
          <EstimatePriceInput
            value={unitPrice}
            saveFunction={() => undefined}
            readOnly={!row.isCustom}
            minWidth={125}
            isInvalid={isClientView && isMarginNegativeOrZero}
          />
        );
      },
    },
    {
      title: GetTitle(t('common.quantity', 'Quantity')),
      dataIndex: 'quantity',
      width: '10vw',
      render: function rowSelector(quantity: number, row: LinkedReference) {
        const isMarginNegativeOrZero = Number(row.margin) <= 0;
        return (
          <QuantityInput
            value={quantity}
            saveFunction={handleUpdate('QUANTITY', row.itemId)}
            removeRow={() => removeLinkedRow(row.itemId)}
            isInvalid={isClientView && isMarginNegativeOrZero}
            design={'v2'}
          />
        );
      },
    },
    {
      title: GetTitle(t('common.discount', 'Discount')),
      dataIndex: 'discount',
      width: '10vw',
      render: function rowSelector(discount: string, row: LinkedReference) {
        const isMarginNegativeOrZero = Number(row.margin) <= 0;
        return (
          <DiscountInput
            value={discount}
            saveFunction={handleUpdate('DISCOUNT', row.itemId)}
            discountType={row.discountType}
            saveDiscountType={handleUpdate('DISCOUNT_TYPE', row.itemId)}
            isInvalid={isClientView && isMarginNegativeOrZero}
          />
        );
      },
    },
    {
      title: GetTitle(t('common.price.vat_excl_price', 'VAT excl.Price')),
      dataIndex: 'priceVatExcluded',
      width: '8vw',

      render: function rowSelector(priceVatExcluded: string, row: LinkedReference) {
        const isMarginNegativeOrZero = Number(row.margin) <= 0;
        return (
          <Flex justify={'center'} align={'center'}>
            <Text type={isClientView && isMarginNegativeOrZero ? 'light_14_bold_red' : 'light_14'}>
              {textFormatter.formatCurrency(isNaN(Number(priceVatExcluded)) ? 0 : priceVatExcluded, currency)}
            </Text>
          </Flex>
        );
      },
    },
    {
      title: GetTitle(t('common.price.vat.title', 'VAT')),
      dataIndex: 'vatPercentage',
      width: '8vw',
      render: function rowSelector(vat: number, row: LinkedReference) {
        const isMarginNegativeOrZero = Number(row.margin) <= 0;
        return (
          <VatInput
            value={vat}
            saveFunction={() => undefined}
            readOnly={!row.isCustom}
            isInvalid={isClientView && isMarginNegativeOrZero}
          />
        );
      },
    },
    ...getCondArrayItem(
      isClientView && {
        title: null,
        dataIndex: '',
        width: 20,
        render: function rowSelector(row: LinkedReference) {
          const isMarginNegativeOrZero = Number(row.margin) <= 0;
          return isMarginNegativeOrZero && <EstimateMarginAlert t={t} />;
        },
      },
    ),
    {
      title: undefined,
      dataIndex: 'empty-column-for-padding',
      width: 30,
      render: function rowSelector() {
        return <></>;
      },
    },
    {
      title: undefined,
      dataIndex: 'empty-column-for-padding',
      width: 30,
      render: function rowSelector() {
        return <></>;
      },
    },
  ];

  if (sparePartsView === GarageView) {
    tableColumns.push(
      {
        title: GetTitle(t('common.price.garage_price', 'Garage Price')),
        dataIndex: 'garagePrice',
        width: '6vw',
        render: function rowSelector(_garagePrice: string, row: LinkedReference) {
          const isMarginNegativeOrZero = Number(row.margin) <= 0;
          return (
            <EstimateGaragePrice
              row={row}
              currency={currency}
              handleUpdate={(_itemId: string, _field: ReferenceField) => (_newValue: string) => undefined}
              isInvalid={isMarginNegativeOrZero}
            />
          );
        },
      },
      {
        title: GetTitle(t('common.price.margin', 'Margin')),
        dataIndex: 'margin',
        width: '6vw',
        render: function rowSelector(_margin: string, row: LinkedReference) {
          const isMarginNegativeOrZero = Number(row.margin) <= 0;
          return (
            <Flex justify={'space-between'} align={'center'} minWidth={70}>
              {isMarginNegativeOrZero ? <EstimateMarginAlert t={t} /> : <Flex minWidth={20} />}
              <EstimateGarageMargin
                estimateId={estimateId}
                row={row}
                currency={currency}
                isInvalid={isMarginNegativeOrZero}
              />
            </Flex>
          );
        },
      },
    );
  }

  return (
    <ExpandedReferenceWrapper>
      <LinkedReferencesBackgroundWrapper>
        <Pipeline size={0} offsetRatio={100} color={theme.color.clear_blue} />
        <ExpandedReferenceDotCircleWrapper>
          <Icon IconComponent={DotCircleIcon} size={8} color={theme.color.clear_blue} noPointer />
        </ExpandedReferenceDotCircleWrapper>
        <MarginBox ml={5} mt={-15}>
          <Text type="base" bold color="clear_blue">
            {t('estimate.linked_references', 'Linked references')}
          </Text>
        </MarginBox>
      </LinkedReferencesBackgroundWrapper>

      <MarginBox mt={20} />
      <STable<LinkedReference>
        columns={tableColumns}
        dataSource={parentReference.linkedReferences}
        pagination={false}
        rowKey={(row) => row.itemId}
        size={'large'}
        showHeader={false}
        style={{ zIndex: 1 }}
      />
    </ExpandedReferenceWrapper>
  );
}
